import { getConfig } from '../config';

export interface RuleFile {
  ruleKey: string;
  name: string;
  description: string;
  content: any;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrUpdateRuleFileRequest {
  name: string;
  description: string;
  content: any;
  createdBy?: string;
  changeNote?: string;
}

export interface ListRuleFilesResponse {
  ruleFiles: RuleFile[];
  lastEvaluatedKey?: string;
}

export interface RuleFileVersion {
  ruleKey: string;
  name: string;
  description: string;
  content: any;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListRuleFileVersionsResponse {
  ruleFiles: RuleFileVersion[];
  lastEvaluatedKey?: string;
}

export interface RollbackRuleFileRequest {
  targetVersion: number;
}

export interface EvaluateRuleRequest {
  input: Record<string, any>;
}

export interface EvaluateRuleResponse {
  result: any;
  executionTime: number;
}

export interface EvaluateLocalRuleRequest {
  content: string;
  input: Record<string, any>;
}

export interface EvaluateLocalRuleResponse {
  result: any;
  executionTime: number;
  trace?: any;
}

export class RuleEngineClient {
  constructor(private readonly apiUrl: string) {}

  private getApiBaseUrl(): string {
    return this.apiUrl;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getConfig().apiKey,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    return response;
  }

  async createOrUpdateRuleFile(ruleKey: string, request: CreateOrUpdateRuleFileRequest): Promise<RuleFile> {
    const response = await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  async getRuleFile(ruleKey: string): Promise<RuleFile> {
    const response = await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}`, {
      method: 'GET',
    });

    return await response.json();
  }

  async listRuleFiles(limit?: number, lastEvaluatedKey?: string): Promise<ListRuleFilesResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);

    const url = `${this.getApiBaseUrl()}/ruleEngine${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });

    return await response.json();
  }

  async listAllRuleFiles(pageLimit = 100, maxPages = 50): Promise<ListRuleFilesResponse> {
    const all: RuleFile[] = [];
    let cursor: string | undefined;
    let pages = 0;
    do {
      const page = await this.listRuleFiles(pageLimit, cursor);
      all.push(...(page.ruleFiles ?? []));
      cursor = page.lastEvaluatedKey;
      pages += 1;
    } while (cursor && pages < maxPages);
    return { ruleFiles: all };
  }

  async listRuleFileVersions(ruleKey: string, limit?: number, lastEvaluatedKey?: string): Promise<ListRuleFileVersionsResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);

    const url = `${this.getApiBaseUrl()}/ruleEngine/${ruleKey}/versions${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });

    return await response.json();
  }

  async rollbackRuleFile(ruleKey: string, request: RollbackRuleFileRequest): Promise<RuleFile> {
    const response = await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}/rollback`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  async evaluateRule(ruleKey: string, request: EvaluateRuleRequest): Promise<EvaluateRuleResponse> {
    const response = await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}/evaluate`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  async evaluateLocalRule(request: EvaluateLocalRuleRequest): Promise<EvaluateLocalRuleResponse> {
    const response = await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/evaluateLocal`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  async reloadRule(ruleKey: string): Promise<void> {
    await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}/reload`, {
      method: 'POST',
    });
  }

  async deleteRuleFile(ruleKey: string): Promise<void> {
    await this.fetchWithAuth(`${this.getApiBaseUrl()}/ruleEngine/${ruleKey}`, {
      method: 'DELETE',
    });
  }
}
